'use client';

import { useState } from 'react';

import { DesktopSideAdBanner } from '@/app/_components/ads';
import { PageNavigation } from '@/components/client';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import { HomeFeedListAdBanner } from '../../(home)/_components/HomeFeedListAdBanner';
import type { HouseholdSize } from '../_hooks/useMedianIncomeState';
import { useMedianIncomeState } from '../_hooks/useMedianIncomeState';
import type { MedianIncomeYear } from '../constants';
import { MEDIAN_INCOME_YEARS } from '../constants';
import { BenefitCards } from './BenefitCards';
import { Calculator } from './Calculator';
import { ConceptGrid } from './ConceptGrid';
import { FaqList } from './FaqList';
import { FinalCta } from './FinalCta';
import { Hero } from './Hero';
import { MobileStickyCalculator } from './MobileStickyCalculator';
import { PolicyCards } from './PolicyCards';
import { RecommendedContents } from './RecommendedContents';
import { TierTable } from './TierTable';
import { YearTabs } from './YearTabs';

interface MedianIncomeSectionProps {
  /** 초기 진입 연도 (SSR에서 결정). */
  initialYear: MedianIncomeYear;
  /** 외부(다른 페이지)에서 진입 시 URL 쿼리로 전달된 초기 계산기 값. */
  initialValues?: { householdSize?: HouseholdSize; percent?: number };
}

/**
 * `/median-income` 본문 — 연도 상태 + 탭 + 전체 레이아웃.
 *
 * 모든 섹션이 하나의 흰 카드(wrapper) 안에 들어가며, 섹션 간 간격은 `gap-32` 로 띄운다.
 * 모바일 mid 광고만 wrapper 바깥에서 그룹 분리 역할.
 *
 * 연도 탭 클릭 시 `useMedianIncomeState` 가 year 변경을 감지해 월 소득은 유지, percent 만
 * 새 baseline 기준으로 재계산.
 */
export const MedianIncomeSection = ({
  initialYear,
  initialValues,
}: MedianIncomeSectionProps) => {
  const [year, setYear] = useState<MedianIncomeYear>(initialYear);
  const state = useMedianIncomeState(year, initialValues);
  const { householdSize, percent, tier } = state;
  const hasMultipleYears = MEDIAN_INCOME_YEARS.length >= 2;

  /**
   * 사용자 인터랙션 시점에만 TierTable 로 자동 스크롤.
   * - effect 기반 (값 변경 감지)은 useMedianIncomeState 초기 default(1인 가구·100%)가
   *   마운트 시 양수라 effect 첫 호출이 즉시 트리거되는 문제가 있어 폐기.
   * - 계산기 컴포넌트에 wrapping 된 state 를 넘기고, TierTable 의 row 클릭은 wrap 하지 않는다
   *   (자기 위치를 그대로 클릭한 것이라 스크롤 의미 없음).
   */
  const scrollToTierTable = () => {
    document
      .getElementById('median-income-tier-table')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const calculatorState = {
    ...state,
    setHouseholdSize: (next: HouseholdSize) => {
      state.setHouseholdSize(next);
      scrollToTierTable();
    },
    onAmountChange: (raw: string) => {
      state.onAmountChange(raw);
      scrollToTierTable();
    },
    onPercentChange: (raw: string) => {
      state.onPercentChange(raw);
      scrollToTierTable();
    },
  };

  return (
    <>
      <PageNavigation title="중위소득 종합 가이드" />
      <div className="bg-Gray-25 py-64 mobile-tablet:bg-Gray-white mobile-tablet:pb-180 mobile-tablet:pt-0">
        <DesktopPageShell
          mainClassName="flex flex-col gap-16"
          rightAside={
            <DesktopSideAdBanner
              className="flex flex-col gap-16 self-stretch mobile-tablet:hidden"
              footer={
                <div className="sticky top-80">
                  <Calculator state={calculatorState} />
                </div>
              }
            />
          }
        >
          <div className="flex flex-col gap-32 rounded-medium border-1 border-Gray-200 bg-Gray-white p-24 mobile-tablet:gap-24 mobile-tablet:rounded-none mobile-tablet:border-0 mobile-tablet:px-0 mobile-tablet:py-16">
            {hasMultipleYears ? (
              <YearTabs selectedYear={year} onChange={setYear} />
            ) : null}
            <Hero year={year} />

            <TierTable
              year={year}
              householdSize={householdSize}
              activeTier={tier}
              onTierClick={(tierPercent) =>
                state.onPercentChange(String(tierPercent))
              }
            />
            <PolicyCards year={year} activeTier={tier} />

            <div className="-mx-24 overflow-hidden mobile-tablet:mx-0">
              <HomeFeedListAdBanner />
            </div>

            <BenefitCards year={year} percent={percent} />

            <div className="-mx-24 overflow-hidden mobile-tablet:mx-0">
              <HomeFeedListAdBanner />
            </div>

            <ConceptGrid year={year} />
            <RecommendedContents year={year} />

            <div className="-mx-24 overflow-hidden mobile-tablet:mx-0">
              <HomeFeedListAdBanner />
            </div>

            <FaqList year={year} />
            <FinalCta />
          </div>
        </DesktopPageShell>
      </div>

      <MobileStickyCalculator state={calculatorState} />
    </>
  );
};
